import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { interval } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { CustomerUtils } from '../../../utils';
import {
  APP_MODE,
  Appointment,
  BusinessObject,
  Order,
  REFRESH_DATA_INTERVAL,
  ResponseError,
  Staff,
} from '../../../models';
import { DialogService, ErrorHandlerServiceService } from '../../../services';
import {
  CalendarAppointmentCancellationDialogComponent,
  CalendarAppointmentCancellationDialogComponentData,
} from '../calendar-appointment-cancellation-dialog/calendar-appointment-cancellation-dialog.component';
import { LibCoreLayoutModule } from '../../../ui/core-layout';
import { CalendarAppointmentDetailsCardComponent } from '../calendar-appointment-details-card/calendar-appointment-details-card.component';
import { CalendarAppointmentChatBoxComponent } from '../calendar-appointment-chat-box/calendar-appointment-chat-box.component';
import {
  CalendarAppointmentChatBoxDialogComponent,
  CalendarAppointmentChatBoxDialogComponentData,
} from '../calendar-appointment-chat-box-dialog/calendar-appointment-chat-box-dialog.component';
import { CalendarStorageService } from '../services/calendar-storage.service';
import { CoreCalendarEntryComponent } from '../core/core-calendar-entry.component';
import { CalendarService } from '../services/calendar.service';
import { PipesModule } from '../../../pipes';

@Component({
  selector: 'lib-calendar-appointment-details-page',
  standalone: true,
  imports: [
    CommonModule,
    LibCoreLayoutModule,
    TranslateModule,
    PipesModule,
    CalendarAppointmentDetailsCardComponent,
    CalendarAppointmentChatBoxComponent,
    // material design
    MatButtonModule,
    MatIconModule,
    MatProgressBarModule,
  ],
  templateUrl: './calendar-appointment-details-page.component.html',
  styleUrl: './calendar-appointment-details-page.component.scss',
})
export class CalendarAppointmentDetailsPageComponent
  extends CoreCalendarEntryComponent
  implements AfterViewInit
{
  private readonly FLOATING_BUTTON_HEIGHT_PX = 66;

  @Input() set appointmentId(id: number) {
    this.initState(id);
  }

  @Output() appointmentFetched = new EventEmitter<Appointment>();
  @Output() appointmentNotFound = new EventEmitter<void>();
  @Output() businessObjectFetched = new EventEmitter<BusinessObject>();

  @ViewChild('fillRemainingHeightEl') fillRemainingHeightEl!: ElementRef;

  @HostListener('window:resize')
  onResize(): void {
    this.calculateRemainingHeight();
  }

  public businessObject: BusinessObject;
  public appointment: Appointment;
  public staff: Staff;

  public isLoading = false;

  private refreshInterval = interval(REFRESH_DATA_INTERVAL);

  constructor(
    protected calendarStorageService: CalendarStorageService,
    private errorHandlerServiceService: ErrorHandlerServiceService,
    private dialogService: DialogService,
    private calendarService: CalendarService
  ) {
    super(calendarStorageService);

    this.refreshInterval
      .pipe(takeUntil(this.componentDestroyed))
      .subscribe(() => {
        this.initState(this.appointment?.id);
      });
  }

  ngAfterViewInit() {
    setTimeout(() => this.calculateRemainingHeight(), 100);
  }

  public isEditActionForbiddenForStaffApp(): boolean {
    return CustomerUtils.isEditActionForbiddenForStaffApp(
      this.mode,
      this.appointment?.staffId,
      this.calendarStorageService.getCalendarAdditionalConfig()?.primaryStaffId,
      this.calendarStorageService.getCalendarAdditionalConfig()
        ?.forbiddenActions
    );
  }

  public shouldDisplayAdvancedInfo(): boolean {
    return this.mode !== APP_MODE.BOOKING;
  }

  public openChatbox(): void {
    const dialogRef = this.dialogService.openDialog(
      CalendarAppointmentChatBoxDialogComponent,
      this.dialogService.createDefaultDialogConfig({
        businessObject: this.businessObject,
        appointment: this.appointment,
        mode: this.mode,
        calendarAdditionalConfig: this.calendarAdditionalConfig,
      } as CalendarAppointmentChatBoxDialogComponentData)
    );

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.componentDestroyed))
      .subscribe(() => {
        this.reloadAppointment();
      });
  }

  public isAbleToCancelAppointment(): boolean {
    return (
      CustomerUtils.isAbleToCancelAppointment(this.appointment) &&
      !this.isEditActionForbiddenForStaffApp()
    );
  }

  public cancelAppointment(): void {
    if (this.isLoading || !this.isAbleToCancelAppointment()) {
      return;
    }

    const dialogRef = this.dialogService.openDialog(
      CalendarAppointmentCancellationDialogComponent,
      this.dialogService.createDefaultDialogConfig({
        mode: this.mode,
        businessObject: this.businessObject,
        appointment: this.appointment,
      } as CalendarAppointmentCancellationDialogComponentData)
    );

    dialogRef
      .afterClosed()
      .pipe(filter((isCancelled) => !!isCancelled))
      .pipe(takeUntil(this.componentDestroyed))
      .subscribe((isCancelled) => {
        if (isCancelled) {
          this.reloadAppointment();
        }
      });
  }

  public async checkin() {
    if (this.isLoading || !this.businessObject?.id || !this.appointment?.id) {
      return;
    }

    try {
      this.isLoading = true;
      const order: Order = await this.calendarService.quickCheckin(
        this.businessObject.id,
        this.appointment.owner,
        this.appointment.id
      );

      if (order?.id) {
        this.appointment = await this.calendarService.getAppointmentById(
          this.appointment.id
        );
        this.appointmentFetched.emit(this.appointment);
      }
    } catch (e) {
      this.errorHandlerServiceService.handleError(e);
    } finally {
      this.isLoading = false;
    }
  }

  private async initState(appointmentId: number) {
    if (!appointmentId) {
      return;
    }

    try {
      this.isLoading = true;

      this.appointment = await this.calendarService.getAppointmentById(
        appointmentId
      );
      this.appointmentFetched.emit(this.appointment);

      if (this.appointment?.locationId) {
        this.businessObject = await this.calendarService.getBusinessObjectById(
          this.appointment?.locationId
        );

        this.businessObjectFetched.emit(this.businessObject);
      }

      if (this.appointment?.staffId) {
        const staffs =
          await this.calendarService.getAllStaffsOfBoForOnlineBooking(
            this.businessObject?.id
          );
        this.staff = staffs?.find((s) => s.id === this.appointment?.staffId);
      }
    } catch (e: any) {
      const error: ResponseError = e?.error;
      if (error?.status === 404) {
        this.appointmentNotFound.emit();
      } else {
        this.errorHandlerServiceService.handleError(e);
      }
    } finally {
      this.isLoading = false;
      this.calculateRemainingHeight();
    }
  }

  private async reloadAppointment() {
    try {
      this.isLoading = true;
      this.appointment = await this.calendarService.getAppointmentById(
        this.appointment.id
      );
      this.calendarStorageService.setRecentlyUpdatedAppointment(
        this.appointment
      );
      this.appointmentFetched.emit(this.appointment);
    } catch (e) {
      this.errorHandlerServiceService.handleError(e);
    } finally {
      this.isLoading = false;
      this.calculateRemainingHeight();
    }
  }

  private calculateRemainingHeight(): void {
    if (!this.fillRemainingHeightEl) {
      return;
    }

    const divEl = this.fillRemainingHeightEl.nativeElement as HTMLElement;

    const offsetTop = divEl.getBoundingClientRect().top + window.scrollY;
    const remainingHeight =
      window.innerHeight - offsetTop - this.FLOATING_BUTTON_HEIGHT_PX;
    divEl.style.height = `${remainingHeight}px`;
  }
}
